import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098TInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1098-T instructions for Tax Year 2025"
          description="Get clear instructions for Form 1098-T for 2025. TaxZerone helps you report tuition payments and education-related credits correctly and stay IRS-compliant."
          keywords=""
          canonical="resources/information-returns/form-1098-t-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-T</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Important information and instructions to file form 1098-T
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098tinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-t-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-T at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Form 1098-T</strong>
                        {
                          ' reports qualified tuition and related expenses paid by students or amounts billed by eligible educational institutions.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Student copies
                        </strong>
                        {' must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {' following the calendar year being reported. The '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS filing deadline
                        </strong>
                        {' is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {', including institution and student details, '}
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        (Payments Received for Qualified Tuition and Related
                        Expenses)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Reserved for Future Use)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>
                        {' (Reserved for Future Use)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Adjustments Made for a Prior Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>
                        {' (Scholarships or Grants)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        {
                          '(Adjustments to Scholarships or Grants for a Prior Year),'
                        }{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 7 '}</strong>
                        (Checkbox for Amounts for an Academic Period Beginning
                        January-March of the Following Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 8 '}</strong>
                        (Graduate Student Checkbox)
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 9 '}</strong>
                        (Academic Program Offered by a Recognized Educational
                        Organization)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 10 '}</strong>
                        (Insurance Contract Reimbursements or Refunds) .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or incomplete filing of Form 1098-C may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the organization meets electronic filing
                        obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies.'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1098-T, Tuition Statement, is an information report used
                  by the student or their parent or guardian to claim a tax
                  credit while filing Form 1040 or 1040-SR. This form should be
                  filed by the eligible educational institution with the IRS for
                  every student enrolled in the institution. Student copies
                  should be sent to each student. Additionally, if the student
                  claimed a refund or reimbursement using insurance, the insurer
                  must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1098-T
                  </Link>{' '}
                  with the IRS and furnish a copy to the student.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Eligible educational institutions
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The accredited public, nonprofit, and private postsecondary
                  institutions that provide valid accredited degrees or
                  certificates and the institution is eligible to participate in
                  the Department of Education’s student aid programs.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Details required to file form 1098-T
                </Typography>
                <Box component="ul" sx={{ mb: 2, ' & li': { mb: 1.5 } }}>
                  <li>Qualified tuition and related expenses</li>
                  <li>Reimbursements or refunds</li>
                  <li>Scholarships or grants (optional)</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Expenses That Do Not Qualify as Tuition Expenses
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Expenses covered for the course or education regarding
                    sports, games, or hobbies unless it is part of the degree or
                    certification program
                  </li>
                  <li>
                    Expenses cover student health fees, transportation, and
                    personal, living, or family expenses.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates in filing form 1098-T
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To IRS
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <b> E-Filing:</b> The IRS encourages the institution to file
                    form 1098-T using the electronic filing method. Electronic
                    filing of form 1098-T is mandatory if the institute files
                    more than 10 information returns.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 31, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <b> Paper filing:</b> Institutions can file using the paper
                    filing method if they are filing less than 10 information
                    returns for the tax year.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To Student
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  An institution should send the student copy to each student
                  enrolled in the institution before the deadline.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Deadline for the Tax year 2025:{' '}
                  <span style={{ color: '#c1121f', fontWeight: 700 }}>
                    February 2, 2026
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  ðŸ‘‰ For detailed information on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    1098 t deadline and related penalties,
                  </span>{' '}
                  visit our{' '}
                  <Link
                    href="/resources/information-returns/form-1098-series-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    1098-T Due Date
                  </Link>{' '}
                  page.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 4, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Filing the forms before the deadline is important
                  to avoid IRS penalties.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step by Step instructions to file form 1098-T
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Filer’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-filer-img.webp" />
                  <source srcSet="/assets/images/1098-t-filer-img.png" />
                  <img
                    src="/assets/images/1098-t-filer-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Filer’s details"
                    width={525}
                    height={208}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the filer’s name, address, and telephone number along
                  with the Filer Employer’s Identification Number. Mostly the
                  filer of form 1098-T is an institution or authorized person
                  who collects tuition and other related expenses on behalf of
                  the institution.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the student&apos;s details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/1098-t-stin-img.webp" />
                  <source srcSet="/assets/images/1098-t-stin-img.png" />
                  <img
                    src="/assets/images/1098-lender-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the student's details"
                    width={264}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the Student’s TIN box if the institution has obtained
                  the student&apos;s SSN or other Tax identification Number
                  using form W-9s or through Financial aid application.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-studend-img.webp" />
                  <source srcSet="/assets/images/1098-t-studend-img.png" />
                  <img
                    src="/assets/images/1098-t-studend-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the student's details"
                    width={524}
                    height={182}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the student’s name, and address if it is a permanent or
                  long-term address. Temporary addresses can be provided only
                  when there is no permanent address.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-service-img.webp" />
                  <source srcSet="/assets/images/1098-t-service-img.png" />
                  <img
                    src="/assets/images/1098-t-service-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the student's details"
                    width={325}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the institute has more than one account with the same
                  student, the account number along with the service provider
                  name must be entered. The IRS recommends that institutions
                  mention the account number even though there is only one
                  account between them.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Student Tuition fee details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Payments Received for Qualified Tuition and Related
                  Expenses{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-payment-img.webp" />
                  <source srcSet="/assets/images/1098-t-payment-img.png" />
                  <img
                    src="/assets/images/1098-t-payment-img.png"
                    loading="lazy"
                    alt="Box 1: Payments Received for Qualified Tuition and Related
                  Expenses"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total qualified tuition fees received for the tax
                  year. Do not deduct scholarship payments or grants from this
                  amount. However, subtract any refunds or reimbursements issued
                  during the same period. Additionally, include any overdue
                  payments collected this year for a prior tax year
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2 & 3: Reserved
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/1098-t-reserved2-img.webp" />
                    <source srcSet="/assets/images/1098-t-reserved2-img.png" />
                    <img
                      src="/assets/images/1098-t-reserved2-img.png"
                      loading="lazy"
                      alt="Box 2 & 3: Reserved"
                      width={217}
                      height={80}
                      className="proportional-img"
                    />
                  </picture>
                </Box>
                <picture>
                  <source srcSet="/assets/images/1098-t-reserved3-img.webp" />
                  <source srcSet="/assets/images/1098-t-reserved3-img.png" />
                  <img
                    src="/assets/images/1098-t-reserved3-img.png"
                    loading="lazy"
                    alt="Box 2 & 3: Reserved"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Box 2 an d 3 require no details as it is reserved for future
                  use.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Adjustments Made for a Prior Year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-adjustment-img.webp" />
                  <source srcSet="/assets/images/1098-t-adjustment-img.png" />
                  <img
                    src="/assets/images/1098-t-adjustment-img.png"
                    loading="lazy"
                    alt="Box 4: Adjustments Made for a Prior Year"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount refunded or reimbursed in the tax year for
                  the previous overpayment of tuition fees or other expenses
                  made after 2002 and also the reductions in charges made for
                  qualified tuition and related expenses made during the tax
                  year that were reported for any previous year after 2002.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Scholarships or Grants
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-scholarship-img.webp" />
                  <source srcSet="/assets/images/1098-t-scholarship-img.png" />
                  <img
                    src="/assets/images/1098-t-scholarship-img.png"
                    loading="lazy"
                    alt="Box 5: Scholarships or Grants"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount the institution administered and processed as
                  scholarships or grants for the student eligible tuition fees
                  and other related expenses. The scholarship includes all
                  third-party payments received for the expense of the student
                  generally like the Department of Veterans Affairs, the
                  Department of Defense, civic and religious organizations, and
                  nonprofit entities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Adjustments to Scholarships or Grants for a Prior Year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-grants-img.webp" />
                  <source srcSet="/assets/images/1098-t-grants-img.png" />
                  <img
                    src="/assets/images/1098-t-grants-img.png"
                    loading="lazy"
                    alt="Box 6: Adjustments to Scholarships or Grants for a Prior Year"
                    width={217}
                    height={105}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the reduction in the amount of scholarship or grant that
                  needs to be reported for the previous tax year after 2002.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Checkbox for Amounts for an Academic Period Beginning
                  in January Through March of 2026
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-academic-img.webp" />
                  <source srcSet="/assets/images/1098-t-academic-img.png" />
                  <img
                    src="/assets/images/1098-t-academic-img.png"
                    loading="lazy"
                    alt="Box 7:  Checkbox for Amounts for an Academic Period Beginning in January Through March of 2025"
                    width={245}
                    height={117}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if you have made any payment for the eligible
                  tuition fees and related expenses for the academic year
                  starting between January and March 2025.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Check if at Least Half-Time Student
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-half-time-img.webp" />
                  <source srcSet="/assets/images/1098-t-half-time-img.png" />
                  <img
                    src="/assets/images/1098-t-half-time-img.png"
                    loading="lazy"
                    alt="Box 8: Check if at Least Half-Time Student"
                    width={203}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the student is at least a half-time student.
                  The half-time students will only have half the part of the
                  workload of the full academic year and the workload should
                  exceed the standards set by the Department of Education.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Check if a Graduate Student
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-graduate-img.webp" />
                  <source srcSet="/assets/images/1098-t-graduate-img.png" />
                  <img
                    src="/assets/images/1098-t-graduate-img.png"
                    loading="lazy"
                    alt="Box 9: Check if a Graduate Student "
                    width={217}
                    height={52}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the student is enrolled in a graduate-level
                  degree or certification program or leads to graduate-level
                  credentials.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Insurance Contract Reimbursements or Refunds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-t-refund-img.webp" />
                  <source srcSet="/assets/images/1098-t-refund-img.png" />
                  <img
                    src="/assets/images/1098-t-refund-img.png"
                    loading="lazy"
                    alt="Box 10:  Insurance Contract Reimbursements or Refunds"
                    width={217}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the student received any insurance refund or
                  reimbursement concerning the qualified tuition fees or related
                  expenses.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to simplify form 1098-T filing?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File form 1098-T at affordable cost of <b>$2.49</b>/per form
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              Save more by filing big!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File form 1098-T in bulk and see the pricing go down to{' '}
              <b>$0.59</b>/per form
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098tinstructions`}
              >
                E-File Form 1098-T
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098TInstructions;
